import { useState, useRef, useEffect } from "react";
import { UserInfo } from "../types";
import { removeStored } from "../lib/safeStorage";

/**
 * Minimal onboarding/auth-gating state for App.
 *
 * The post-install onboarding flow (install / pin / demo / "you're protected") and the
 * usage survey + plan-recommendation steps have been removed: after auth, users go straight
 * to the dashboard, where new users see the onboarding carousel. This hook now only tracks
 * the "auth resolved" gate, the forgot-password toggle, and forces light theme while signed
 * out (the auth screen is light-only).
 */
export function useOnboarding(args: {
  authInitialized: boolean;
  userInfo: UserInfo | null;
  setForceLight: (v: boolean) => void;
}) {
  const { authInitialized, userInfo, setForceLight } = args;

  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const authGenerationRef = useRef(0);

  // Post-install is gone — kept as a constant/no-op so App's render gates and the join flow
  // (which is handed `updateIsPostInstall`) still type-check.
  const isPostInstall = false;
  const updateIsPostInstall = (_val: boolean) => { /* no-op: post-install flow removed */ };

  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Force light theme while signed out / before the dashboard is ready; the dashboard itself
  // respects the user's chosen theme.
  const isInOnboarding = authInitialized && (!userInfo || !onboardingChecked);
  useEffect(() => {
    setForceLight(isInOnboarding);
  }, [isInOnboarding, setForceLight]);

  // Clear any legacy onboarding flags a prior build may have left in localStorage.
  // ENG-3411: through lib/safeStorage. App's auth listener calls this on every signed-out answer, just
  // before it marks auth as initialized, so a throw here where site data is blocked left the console on
  // its loading spinner for good.
  const resetOnboarding = () => {
    removeStored("onboardingState");
    removeStored("onboardingCompleted");
  };

  return {
    onboardingChecked,
    isPostInstall,
    showForgotPassword,
    setOnboardingChecked,
    updateIsPostInstall,
    setShowForgotPassword,
    authGenerationRef,
    resetOnboarding,
  };
}
